/**
 * useProductForm Hook - Story 6.3: Create Add Product Form
 * Multi-step form state management hook for product creation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ProductFormData,
  UseProductFormReturn,
  ProductDetailsFormData,
  PricingFormData,
  ImagesFormData,
  Image,
  CreateProductResponse,
  FormErrors,
  ProductCategory,
} from '../types/productForm.types';
import {
  productDetailsSchema,
  pricingSchema,
  imagesSchema,
  completeProductFormSchema,
  extractFieldErrors,
} from '../schemas/productForm.schema';
import {
  createProductWithImages,
  updateProductWithImages,
  ProductFormApiError,
} from '../services/productFormService';
import { toast } from '@/lib/toast';

// ============================================================================
// Constants
// ============================================================================

const TOTAL_STEPS = 3;

const INITIAL_FORM_DATA: ProductFormData = {
  name: '',
  category: ProductCategory.OTHER,
  description: '',
  price: 0,
  unit: '',
  quantity: 0,
  minimumOrderQuantity: undefined,
  images: [],
  state: '',
  district: '',
  pincode: '',
  address: '',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract data for a specific step from the complete form data
 * @param formData - Complete form data
 * @param step - Step number (1-3)
 * @returns Partial form data for the specified step
 */
function extractStepData(
  formData: ProductFormData,
  step: number
): ProductDetailsFormData | PricingFormData | ImagesFormData | Record<string, never> {
  switch (step) {
    case 1:
      return {
        name: formData.name,
        category: formData.category,
        description: formData.description,
      } as ProductDetailsFormData;
    case 2:
      return {
        price: formData.price,
        unit: formData.unit,
        quantity: formData.quantity,
        minimumOrderQuantity: formData.minimumOrderQuantity,
      } as PricingFormData;
    case 3:
      return {
        images: formData.images,
      } as ImagesFormData;
    default:
      return {};
  }
}

/**
 * Get the validation schema for a specific step
 * @param step - Step number (1-3)
 * @returns Zod schema for the specified step
 */
function getStepSchema(step: number): z.ZodSchema {
  const stepSchemas: z.ZodSchema[] = [
    productDetailsSchema,
    pricingSchema,
    imagesSchema,
  ];

  if (step >= 1 && step <= stepSchemas.length) {
    return stepSchemas[step - 1];
  }

  // Return a permissive schema for unknown steps
  return z.object({});
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useProductForm Hook Parameters
 */
export interface UseProductFormParams {
  /** Mode: 'create' for new product or 'edit' for updating existing product */
  mode?: 'create' | 'edit';
  /** Product ID (required for edit mode) */
  productId?: string;
  /** Initial form data (pre-filled data for edit mode) */
  initialData?: ProductFormData;
  /** Optional callback called with product ID after successful creation/update */
  onSuccess?: (productId: string) => void;
}

/**
 * useProductForm Hook
 * Manages multi-step product creation/editing form state, validation, and submission
 *
 * @param params - Hook parameters including mode, productId, initialData, and onSuccess callback
 * @returns UseProductFormReturn interface with form state and methods
 *
 * @example
 * // Create mode
 * const form = useProductForm({
 *   mode: 'create',
 *   onSuccess: (productId) => router.push(`/marketplace/products/${productId}`)
 * });
 *
 * @example
 * // Edit mode
 * const form = useProductForm({
 *   mode: 'edit',
 *   productId: 'product-123',
 *   initialData: existingProductData,
 *   onSuccess: (productId) => toast.success('Product updated!')
 * });
 */
export function useProductForm(
  params?: UseProductFormParams | ((productId: string) => void)
): UseProductFormReturn {
  // Handle legacy function signature for backward compatibility
  const normalizedParams: UseProductFormParams = typeof params === 'function'
    ? { mode: 'create', onSuccess: params }
    : params || { mode: 'create' };

  const {
    mode = 'create',
    productId,
    initialData,
    onSuccess,
  } = normalizedParams;

  // ============================================================================
  // State & Hooks
  // ============================================================================

  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const initialFormData = initialData || INITIAL_FORM_DATA;
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const formRef = useRef<UseFormReturn<ProductFormData> | null>(null);
  const initialDataRef = useRef<ProductFormData>(initialFormData);

  // Log mode on mount
  useEffect(() => {
    console.log('[useProductForm] Hook initialized:', {
      mode,
      productId,
      hasInitialData: !!initialData,
    });
  }, [mode, productId, initialData]);

  // ============================================================================
  // React Hook Form Integration
  // ============================================================================

  const form = useForm<ProductFormData>({
    resolver: zodResolver(completeProductFormSchema),
    mode: 'onChange',
    defaultValues: initialFormData,
  });

  // Store form reference
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Track unsaved changes (compare with initialData instead of empty values)
  useEffect(() => {
    const hasChanges =
      formData.name !== initialDataRef.current.name ||
      formData.description !== initialDataRef.current.description ||
      formData.category !== initialDataRef.current.category ||
      formData.price !== initialDataRef.current.price ||
      formData.unit !== initialDataRef.current.unit ||
      formData.quantity !== initialDataRef.current.quantity ||
      formData.minimumOrderQuantity !== initialDataRef.current.minimumOrderQuantity ||
      formData.images.length !== initialDataRef.current.images.length ||
      formData.state !== initialDataRef.current.state ||
      formData.district !== initialDataRef.current.district ||
      formData.pincode !== initialDataRef.current.pincode ||
      formData.address !== initialDataRef.current.address;

    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  // ============================================================================
  // Validation Methods
  // ============================================================================

  /**
   * Validate the current step's data
   * @returns Promise resolving to true if valid, false otherwise
   */
  const isCurrentStepValid = useCallback(async (): Promise<boolean> => {
    setIsValidating(true);
    const newErrors: FormErrors = {};

    try {
      const currentSchema = getStepSchema(currentStep);
      const stepData = extractStepData(formData, currentStep);

      await currentSchema.parseAsync(stepData);
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          const path = err.path.join('.');
          newErrors[path] = err.message;
        });
      }
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [currentStep, formData]);

  // ============================================================================
  // Navigation Methods
  // ============================================================================

  /**
   * Navigate to the next step after validating the current step
   * @returns Promise resolving to true if navigation succeeded
   */
  const goToNextStep = useCallback(async (): Promise<boolean> => {
    setIsValidating(true);
    const newErrors: FormErrors = {};

    try {
      // Already at last step
      if (currentStep === TOTAL_STEPS) {
        return false;
      }

      const currentSchema = getStepSchema(currentStep);
      const stepData = extractStepData(formData, currentStep);

      // Validate current step data
      await currentSchema.parseAsync(stepData);

      // Clear errors for this step's fields
      const stepFields = Object.keys(stepData);
      setErrors((prev) => {
        const updated = { ...prev };
        stepFields.forEach((key) => {
          delete updated[key];
        });
        return updated;
      });

      // Move to next step
      if (currentStep < TOTAL_STEPS) {
        setCurrentStep(currentStep + 1);
        return true;
      }

      return false;
    } catch (error) {
      // Capture validation errors
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          const path = err.path.join('.');
          newErrors[path] = err.message;
        });
        setErrors((prev) => ({ ...prev, ...newErrors }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general: 'Validation failed. Please check your data.',
        }));
      }
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [currentStep, formData]);

  /**
   * Navigate to the previous step
   */
  const goToPreviousStep = useCallback((): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Clear errors when going back
      setErrors({});
    }
  }, [currentStep]);

  /**
   * Navigate to a specific step (only if it's been visited or is adjacent)
   * @param step - Target step number
   */
  const goToStep = useCallback(
    (step: number): void => {
      if (step >= 1 && step <= TOTAL_STEPS && step <= currentStep) {
        setCurrentStep(step);
        setErrors({});
      }
    },
    [currentStep]
  );

  // ============================================================================
  // Form Submission
  // ============================================================================

  /**
   * Submit the complete form (create or update based on mode)
   * @returns Promise resolving to created/updated product or undefined on error
   */
  const submitForm = useCallback(async (): Promise<CreateProductResponse> => {
    setIsSubmitting(true);
    const newErrors: FormErrors = {};

    console.log('[useProductForm] Submitting form:', {
      mode,
      productId,
      hasImages: formData.images.length,
    });

    try {
      // Validate complete form before submission
      await completeProductFormSchema.parseAsync(formData);

      // Extract non-image form data
      const productRequest = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        price: formData.price,
        unit: formData.unit,
        quantity: formData.quantity,
        minimumOrderQuantity: formData.minimumOrderQuantity,
        state: formData.state,
        district: formData.district,
        pincode: formData.pincode,
        address: formData.address || '',
      };

      // Extract new image files (File objects) that haven't been uploaded yet
      const imageFiles = formData.images
        .filter((img): img is Image & { file: File } => img.file instanceof File)
        .map((img) => img.file);

      // Extract URLs of already-uploaded images (from immediate upload feature or existing images)
      // These have server URLs (in serverUrl or preview) but no file object
      const uploadedImageUrls = formData.images
        .filter((img) => {
          // Include images that are NOT pending file uploads
          if (img.file instanceof File) return false;
          // Must have a server URL (either in serverUrl or a non-data/non-blob preview)
          const url = img.serverUrl || img.preview;
          return url
            && !url.startsWith('blob:')
            && !url.startsWith('data:');
        })
        .map((img) => {
          // Prefer serverUrl (set after immediate upload), fall back to preview (existing images)
          const url = img.serverUrl || img.preview;
          return url;
        });

      // In edit mode: ONLY send the images user currently has selected
      // This includes: already-uploaded images + new files to upload
      // Old images NOT in this list will be deleted
      const existingImageUrls = uploadedImageUrls;

      console.log('[useProductForm] Prepared request:', {
        mode,
        productId,
        newFileCount: imageFiles.length,
        uploadedImageURLCount: uploadedImageUrls.length,
        uploadedImageUrls: uploadedImageUrls,
        totalImagesToSend: existingImageUrls.length,
        totalImagesInForm: formData.images.length,
        imageDetails: formData.images.map((img, i) => ({
          index: i,
          hasFile: img.file instanceof File,
          previewType: img.preview?.substring(0, 30),
          serverUrl: img.serverUrl?.substring(0, 60),
        })),
      });

      let result;

      if (mode === 'edit') {
        // Edit mode: Update existing product
        // New behavior: REPLACE all images with only what user selected
        // Any image not explicitly re-selected by user will be deleted
        if (!productId) {
          throw new Error('Product ID is required for edit mode');
        }

        // Only send the images user explicitly selected
        // This includes: new files to upload + already-uploaded image URLs
        // Old images NOT in this list will be automatically deleted
        result = await updateProductWithImages(productId, productRequest, imageFiles, existingImageUrls);

        // Check for partial errors (product updated but images failed)
        if (result.partialError) {
          toast.error(
            'Product updated, but some images failed to upload. You can add more images later.'
          );
        } else {
          toast.success('Product updated successfully! Refreshing list...');
        }

        // Invalidate product list query to trigger auto-refresh
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } else {
        // Create mode: Create new product
        result = await createProductWithImages(productRequest, imageFiles);

        // Check for partial errors (product created but images failed)
        if (result.partialError) {
          toast.error(
            'Product created, but some images failed to upload. You can add more images later.'
          );
        } else {
          toast.success('Product created successfully!');
        }
      }

      // Mark as no longer having unsaved changes
      setHasUnsavedChanges(false);

      // Call success callback with product ID
      onSuccess?.(result.product.id);

      return result.product;
    } catch (error) {
      // Handle validation errors from Zod
      if (error instanceof z.ZodError) {
        const fieldErrors = extractFieldErrors(error);
        Object.assign(newErrors, fieldErrors);
        setErrors(newErrors);
        toast.error('Please check the form for errors.');
        throw error;
      }

      // Handle API errors
      if (error instanceof ProductFormApiError) {
        if (error.fieldErrors) {
          Object.assign(newErrors, error.fieldErrors);
        }
        newErrors['general'] = error.message;
        setErrors(newErrors);
        toast.error(error.message);
        throw error;
      }

      // Handle unknown errors
      const errorMessage = mode === 'edit'
        ? 'Failed to update product. Please try again.'
        : 'Failed to create product. Please try again.';
      newErrors['general'] = errorMessage;
      setErrors(newErrors);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, mode, productId, onSuccess]);

  // ============================================================================
  // Form Data Management
  // ============================================================================

  /**
   * Update form data with partial values
   * @param data - Partial form data to merge
   */
  const updateFormData = useCallback((data: Partial<ProductFormData>): void => {
    setFormData((prev) => ({ ...prev, ...data }));

    // Clear errors for updated fields
    const updatedFields = Object.keys(data);
    setErrors((prev) => {
      const updated = { ...prev };
      updatedFields.forEach((key) => {
        delete updated[key];
      });
      delete updated['general'];
      return updated;
    });
  }, []);

  /**
   * Reset the form to initial state (uses initialData in edit mode)
   */
  const resetForm = useCallback((): void => {
    setFormData(initialDataRef.current);
    setCurrentStep(1);
    setErrors({});
    setHasUnsavedChanges(false);
    form.reset(initialDataRef.current);
  }, [form]);

  /**
   * Clear errors for a specific field or all errors
   * @param field - Optional field name to clear errors for
   */
  const clearErrors = useCallback((field?: string): void => {
    if (field) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    } else {
      setErrors({});
    }
  }, []);

  // ============================================================================
  // Return Value
  // ============================================================================

  return {
    formData,
    currentStep,
    totalSteps: TOTAL_STEPS,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    submitForm,
    resetForm,
    isSubmitting,
    isValidating,
    errors,
    setFormData: updateFormData,
    clearErrors,
    isCurrentStepValid,
    hasUnsavedChanges,
  };
}

// ============================================================================
// Exports
// ============================================================================

export type { UseProductFormReturn, UseProductFormParams };
